<!-- Copyright 2020 Adobe. All rights reserved.
This file is licensed to you under the Apache License, Version 2.0 (the "License");
you may not use this file except in compliance with the License. You may obtain a copy
of the License at http://www.apache.org/licenses/LICENSE-2.0
Unless required by applicable law or agreed to in writing, software distributed under
the License is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR REPRESENTATIONS
OF ANY KIND, either express or implied. See the License for the specific language
governing permissions and limitations under the License. -->

import {Layout} from '@react-spectrum/docs';
export default Layout;

import docs from 'docs:@react-spectrum/textfield';
import {HeaderInfo, PropTable} from '@react-spectrum/docs';
import packageData from '../package.json';

```jsx import
import {TextArea} from '@react-spectrum/textfield';
```

---
category: Forms
---

# TextArea

<p>{docs.exports.TextArea.description}</p>

<HeaderInfo
  packageData={packageData}
  componentNames={['TextArea']}
  sourceData={[]} />

## Example

```tsx example
<TextArea label="Description" />
```

## Value

A TextArea's `value` is empty by default, but an initial, uncontrolled, value can be provided using the `defaultValue` prop.
Alternatively, a controlled value can be provided using the `value` prop.

```tsx example
<TextArea defaultValue="Default text" label="Uncontrolled TextArea" marginEnd="20px" />
<TextArea value="Default text" label="Controlled TextArea" />
```

Placeholder text that describes the expected value or formatting for the TextArea can be provided using the `placeholder` prop. 
Placeholder text will only appear when the TextArea is empty, and should not be used as a substitute for labeling the component with a visible label.

```tsx example
<TextArea placeholder="abc@adobe.com" label="Email" />
```

## Labeling

A visual label should be provided for the TextArea using the `label` prop. If the TextArea is required, the `isRequired` and 
`necessityIndicator` props can be used to show a required state.

```tsx example
<TextArea label="Street address" marginEnd="20px" />
<TextArea label="Street address" isRequired necessityIndicator="icon" marginEnd="20px" />
<TextArea label="Street address" isRequired necessityIndicator="label" marginEnd="20px" />
<TextArea label="Street address" necessityIndicator="label" />
```

### Accessibility

If a visible label isn't specified, an `aria-label` must be provided to the TextArea for 
accessibility. If the field is labeled by a separate element, an `aria-labelledby` prop must be provided using 
the `id` of the labeling element instead.

```tsx example
<label id="label-1">Name</label>
<TextArea aria-labelledby="label-1" marginStart="10px" />
```

### Internationalization

In order to internationalize a TextArea, a localized string should be passed to the `label` or `aria-label` prop.
When the `necessityIndicator` prop is set to `"label"`, a localized string will be provided for `"(required)"` or `"(optional)"` automatically.

```tsx example
<TextArea label="Street address" isRequired necessityIndicator="label" marginEnd="20px" />
<TextArea label="Street address" necessityIndicator="label" />
```

## Events

TextArea accepts an `onChange` prop which is triggered whenever the value is edited by the user. 
For a full list of supported events, see the [Props](#props) table below.

The example below uses `onChange` to update a separate `span` element with the same text entered into the TextArea.
```tsx example
function Example() {
  let [text, setText] = React.useState();

  return (
    <div>
      <TextArea
        placeholder="Enter some text"
        onChange={setText}
        label="Text Mirror"
        marginEnd="20px"
      />
      <span>Mirrored text: {text}</span>
    </div>
  );
}
```

## Validation

TextAreas can display a validation state to communicate to the user whether the current value is valid or invalid. 
Implement your own validation logic in your app and pass either `"valid"` or `"invalid"` to the TextArea via the `validationState` prop.

The example below illustrates how one would validate if the user entered more than 30 characters into the TextArea.
```tsx example
function Example() {
  let defaultValue = '';
  let [isValid, setValid] = React.useState(false);
  let [value, setValue] = React.useState(defaultValue);
  React.useEffect(() => {
    setValid(value.length > 30)
  }, [setValid, value]);

  return (
    <TextArea
      validationState={isValid ? 'valid' : 'invalid'}
      placeholder="Enter a description"
      defaultValue={defaultValue}
      onChange={setValue}
      label="Description"
    />
  );
}
```

## Props

<PropTable component={docs.exports.TextArea} links={docs.links} />

## Visual options

### Quiet 

[View guidelines](https://spectrum.adobe.com/page/text-field/#Quiet)

In addition to changing the visual style of the TextArea, the `isQuiet` prop removes the ability to manually resize the TextArea.
To compensate, the TextArea automatically adjusts its own height to accommodate for additional lines of text.

```tsx example
<TextArea label="Email" placeholder="Email Address" isQuiet />
```

### Disabled

[View guidelines](https://spectrum.adobe.com/page/text-field/#Disabled)

```tsx example
<TextArea label="Email" placeholder="Email Address" isDisabled />
```

### Read only

The `isReadOnly` boolean prop makes the TextArea's text content immutable. Unlike `isDisabled` the TextArea remains focusable 
and the contents can still be copied. See [the MDN docs](https://developer.mozilla.org/en-US/docs/Web/HTML/Attributes/readonly) for more information.

```tsx example
<TextArea label="Email" defaultValue="abc@adobe.com" isReadOnly />
```

### Label alignment

Labels can be aligned to the "start" or the "end" of the TextArea. For left-to-right (LTR) languages, "start" refers to the left most edge
of the TextArea and "end" refers to the right most edge. For right-to-left (RTL) languages, this is flipped.

```tsx example
<TextArea label="Email" labelAlign="start" marginEnd="20px" />
<TextArea label="Email" labelAlign="end" />
```

### Label position

[View guidelines](https://spectrum.adobe.com/page/text-field/#Label-position)

```tsx example
<TextArea label="Email" labelPosition="top" marginEnd="20px" />
<TextArea label="Email" labelPosition="side" />
```

### Custom width

[View guidelines](https://spectrum.adobe.com/page/text-field/#Width)

```tsx example
<TextArea label="Email" width={'400px'} />
```
